/**
 * Training SOP — Project Engineer track.
 *
 * The product owner's framing: RFIs, submittals and the schedule are the PM's, with the PE
 * assisting. But the PE genuinely OWNS one thing day to day — the concrete
 * order queue on QA/QC — so that leads the track rather than trailing the
 * assisting work.
 *
 * ACCESS — CONFIRMED by the product owner 2026-07-25: PEs hold the `ProjectEngineer` role,
 * which carries `projects.edit` and NOTHING ELSE (equipment permissions were
 * explicitly stripped from it in the 2026-06-04 id-band migration). So the
 * boundaries below are stated as fact, not hedged:
 *
 *   CAN  — RFIs, submittals, change orders, QA/QC pour cards, documents,
 *          drawings, daily logs, project fields, notes, phase dates, field
 *          leader assignment, and the mobilization CHECKLIST.
 *   CANNOT — equipment requests, receiving orders, material lines, survey
 *          request edits, the crew schedule, and mobilization PHASES.
 *
 * The checklist/phases split on the same tab is the one that surprises people:
 * the checklist needs projects.edit, the phases need mobilizations.edit.
 *
 * Verified against the code (2026-07-25 sweep) and the product owner's ownership answers.
 */
import { OWNERSHIP, type Track } from '../types';

export const PE_TRACK: Track = {
  id: 'pe',
  label: 'Project Engineer',
  audience:
    'Project Engineers. You own the concrete order queue; you assist the PM on RFIs, submittals and the schedule.',
  suggestedFor: { anyRole: ['ProjectEngineer'] },
  sections: [
    // ── 1. Concrete orders — the PE's own queue ─────────────────────────────
    {
      id: 'pe-concrete',
      title: 'The concrete order queue',
      intro:
        'This is the part of the Project Tracker built for you. The field submits a pre-pour checklist from the site; that submission IS the concrete order, and working it is yours.',
      topics: [
        {
          id: 'pe-queue',
          title: 'Working the queue',
          summary:
            'The QA/QC screen collects every pour card across every job in one place, so you are not hunting job by job. Cards arrive from the field\'s Formstack form and land as pending.',
          steps: [
            'Open **QA/QC** from the top navigation.',
            'Start on **Needs confirmation** — those are orders nobody has placed yet.',
            'Open a card to see the pour: element, date and time, mix, quantity, placement and who is running it.',
          ],
          watchOut: [
            'Cards appear here automatically from the field\'s form. If one is missing, the problem is upstream in Formstack, not here.',
            'The KPI tiles across the top — awaiting confirm, pours next 7 days, CY placed last 7 days, flagged inspections — are the fastest read on whether you are on top of it.',
          ],
          deepLink: '/qaqc',
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'pe-confirm-and-order',
          title: 'Confirming an order with the supplier',
          summary:
            'Confirming records that you have actually placed the order. There is a built-in email that fills in every detail the supplier needs.',
          steps: [
            'Open the card and use **✉ Email vendor** — it opens a message pre-filled with job, element, location, date and time, mix, quantity, placement and field leader.',
            'Add the supplier\'s address, and send it.',
            'Back on the card, hit **Confirm order**.',
          ],
          watchOut: [
            'The **To: line is deliberately empty** — the field\'s form does not capture supplier emails, so you add the address. That is not a bug.',
            'Confirm only works on a pending card. The order of operations is fixed: pending → confirmed → poured.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'pe-mark-poured',
          title: 'Closing a pour out',
          summary:
            'Once concrete is in the ground the card needs closing. Often the field does this for you without either of you thinking about it.',
          steps: [
            'If the field recorded the pour on that day\'s daily log, the card **flips to poured by itself** — including the actual yardage they entered.',
            'If not, open the card and use **Mark poured**.',
            'For a will-call that got absorbed into the main pour, use **Confirmed with main pour** instead — it closes the will-call and drops it off the schedule.',
          ],
          watchOut: [
            'A **"New main pour received"** badge on an open will-call means the field has since firmed it up into a main pour. Nothing happens automatically — you still have to cancel the will-call or mark it covered.',
            'Cancel works on any open card, will-call or not.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'pe-pour-schedule',
          title: 'The pour schedule grid',
          summary:
            'The same information laid out as jobs down the side and days across the top — the view that replaced the spreadsheet. Only days that actually have pours get a column, so it stays readable.',
          steps: [
            'On **QA/QC**, switch to the **Schedule** view.',
            'Use **Upcoming** for the working view, or **Everything** to look back.',
            'Click any chip to open the full card.',
          ],
          table: {
            caption: 'What the chip colors mean',
            headers: ['Chip', 'Meaning'],
            rows: [
              ['White with a black border', 'New — not confirmed yet. Your queue.'],
              ['Yellow', 'Confirmed will-call'],
              ['Orange', 'Confirmed main pour'],
              ['Gray', 'Confirmed mud seal / flowable fill'],
              ['Green', 'Complete'],
              ['Blue, struck through', 'Cancelled'],
            ],
          },
          watchOut: [
            'Pour times show exactly what the field typed on the form — they are not converted between time zones. If a time looks shifted, it is not; do not "correct" it.',
          ],
          deepLink: '/qaqc?view=schedule',
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'pe-fix-cards',
          title: 'Fixing what the field got wrong',
          summary:
            'Forms get typos. You can correct any field on a pour card, and you can attach a card to the right job when the job number on the form did not match anything.',
          steps: [
            'Open the card and edit the fields that are wrong — pour type, element, location, vendor, date, mix, quantity, cost code and the rest.',
            'If the card is not attached to a job, use the job picker to assign it.',
          ],
          watchOut: [
            'Every edit is recorded in the card\'s **Activity** stream with your name against it. That is deliberate — the card is a record, so corrections are visible rather than silent.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'pe-mix-not-approved',
          title: 'When a card says "Mix not approved"',
          summary:
            'Each job has a list of concrete mixes approved for it. If the Mix ID on a card is not on that job\'s list, the card is flagged. It is a prompt to check, not a blocked order.',
          owner: OWNERSHIP.concreteMixDesigns,
          steps: [
            'Open the card — the note under **Mix / quantity** says what was typed and what to do about it.',
            'If it looks like a transcription slip, use **Edit** and correct the Mix ID. A corrected ID that matches an approved mix clears the flag right away.',
            'If the mix looks right and simply has not been approved on this job yet, that is the PM\'s call — ask them to add it to **Approved Mix Designs**.',
            'Either way you can still confirm and pour. Nothing is blocked.',
          ],
          watchOut: [
            'No flag does **not** mean the mix was checked. If the job has no approved mixes recorded yet, nothing is being checked at all — the **Approved Mix Designs** list on the job\'s QA / QC tab tells you which situation you are in.',
            'You can add a mix yourself, but the approval decision belongs to the PM. Adding one you have not been told is approved defeats the point of the check.',
          ],
          deepLinkNote: 'Open a job from Projects, then the QA / QC tab in the left-hand navigation.',
          access: { anyPermission: ['projects.edit'] },
        },
      ],
    },

    // ── 2. Assisting on RFIs and submittals ─────────────────────────────────
    {
      id: 'pe-rfis-submittals',
      title: 'RFIs and submittals',
      intro:
        'These belong to the PM; you assist. In practice that usually means drafting, chasing and logging what comes back.',
      topics: [
        {
          id: 'pe-rfis',
          title: 'Helping run RFIs',
          summary:
            'Log the question, keep track of who owes the answer, and record it when it lands. The RFI number fills itself in with the next in sequence.',
          owner: OWNERSHIP.rfis,
          steps: [
            'On the job\'s **RFIs** tab, create the RFI — subject and question, who it is going to, and a due date.',
            'If there are markups worth keeping, open the saved RFI and add them from its **Job Folder** card. As with submittals, anything going to an outside party is attached in Outlook — the upload is the record.',
            'If the RFI should go out on the company form, use **Export to Excel** on the RFI. It fills in what the RFI holds. Complete the boxes left in yellow, then attach the file to the email.',
            '**Who it goes to** (new, October 2026). Pick **Assigned to** from the people on the job\'s **Contacts** tab; the role labels are still there for a job with no contacts yet. The Outlook draft then starts addressed to them, and you can add anyone else on **CC**, from the job\'s contacts or by typing an address. When you mark it sent, the RFI records who it went to and shows it under "Assignee notified".',
            'Use **Send with Outlook** to open a pre-written draft. When you close it after opening it in Outlook, the Project Tracker asks **Did you send it?** Once the email has gone, answer **Mark as sent** and the time it went (Eastern time). If it has not, choose **Not sent yet**, and use **Mark as sent…** on the RFI later.',
            'When the answer comes back, open the RFI and use **Submit Response & Close**.',
          ],
          watchOut: [
            '**The app never sends email and never chases anyone.** "Send with Outlook" opens a draft that someone still has to send, and the RFI only shows as sent once it is marked sent. There are no automatic reminders — the chasing is yours.',
            'A sent time that is wrong — older RFIs were marked sent when the draft opened — can be fixed with **Edit** beside "Assignee notified", or cleared there with **Mark as not sent**.',
            '"Overdue" appears on its own once an open RFI passes its due date. It is a display state, not something you set.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'pe-submittals',
          title: 'Helping run submittals',
          summary:
            'Track shop drawings, product data, samples and mixes through the reviewer, with a clock running on how long they have had it.',
          owner: OWNERSHIP.submittals,
          steps: [
            'Create the submittal on the job\'s **Submittals** tab — only the title is required.',
            'Open the saved submittal and use its **Review rounds** card to keep the package on the record: the original from the sub goes under **Original package**. The upload appears only once the submittal exists, because the file name is built from the submittal number.',
            'If the submittal should go out under the company transmittal, use **Export to Excel** on the submittal. It fills in what the submittal holds and ticks Contents from its type. Complete the boxes left in yellow and tick Delivery and Response Needed, then attach the file to the email.',
            '**Who it goes to** (new, October 2026). Pick the **Reviewer** from the people on the job\'s **Contacts** tab, or a role as before. The draft starts addressed to them, with anyone else on **CC**. When you mark a send or a revision as sent, its round records who it went to.',
            'Use **Send with Outlook** to open a pre-written draft. When you close it after opening it in Outlook, the Project Tracker asks **Did you send it?** Once the email has gone, answer **Mark as sent** and the time it went (Eastern time). That is what puts it in review and starts the clock. If it has not gone, choose **Not sent yet**, and use **Mark as sent…** on the submittal later.',
            'Log the decision when it returns: Approved, Approved as Noted, Revise & Resubmit, or Rejected. If a marked-up copy came back, add it under **Returned with comments** on that round.',
          ],
          watchOut: [
            'The days-in-review count only starts when the email is marked as sent, and stops on the decision. Creating a submittal, or opening its draft, does not start any clock. A sent time that is wrong — older submittals were marked sent when the draft opened — can be fixed with **Edit** beside "Reviewer notified", or cleared there with **Mark as not sent** when it has had no decision yet.',
            'A Revise & Resubmit sets a 10-day turnaround; a new submittal defaults to 14 days. Use **Send Revision** and enter the day the corrected package came back from the sub, then answer **Did you send it?** the same way: marking it sent opens the next round (Rev 1, Rev 2…) of the same submittal, so you do not create a second one, and it only works once the decision has been logged.',
            '**Uploading to the Job Folder is not how documents reach the reviewer.** Job Folder links do not open for anyone outside E&W, so the files go on the email in Outlook. The upload is the record of what went out, and a marked-up copy that comes back by email is saved under the round it answers.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
      ],
    },

    // ── 3. Documents, drawings, schedule ────────────────────────────────────
    {
      id: 'pe-docs-schedule',
      title: 'Documents, drawings and the schedule',
      topics: [
        {
          id: 'pe-documents',
          title: 'Filing documents and drawings',
          summary:
            'The Documents tab is a live window into the job\'s SharePoint folder, and Drawings is a versioned index of whole drawing sets. Both build the filename for you.',
          steps: [
            'On **Documents**, use Upload — pick "new file" or "new version of" an existing one.',
            'Fill in document number, a short description and a status; the job number and date are added automatically.',
            'For drawings, use the **Drawings** tab and add either a new set or a revision of an existing set.',
          ],
          watchOut: [
            'The two tabs name files differently on purpose — Documents uses underscores, Drawings uses dashes. Do not try to make them match.',
            'If an upload succeeds but tracking fails, the message will say the file is safe in SharePoint. **Do not upload it again** — you will create a duplicate.',
            'Drawings is an index, not a live folder view. A PDF dropped straight into SharePoint will not appear there.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'pe-schedule-support',
          title: 'Keeping the schedule and checklist current',
          summary:
            'The dates and the readiness checklist are the PM\'s, but you will often be the one keeping them honest.',
          owner: OWNERSHIP.schedule,
          steps: [
            'Dates live on **Overview ▸ Schedule & Dates** — start, end, completion percentage and budget hours per phase.',
            'The readiness checklist is on the **Mobilization** tab. Set each item and hit **Save checklist**.',
          ],
          watchOut: [
            '"Schedule Confirmed" cannot be set by hand — it turns Yes by itself once the job has crew on the manpower schedule.',
            'An item marked **N/A counts as done**, same as Yes. That is intentional.',
            'The Director of Preconstruction watches the checklist across every job, so gaps show.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
      ],
    },

    // ── 4. Access boundaries ────────────────────────────────────────────────
    {
      id: 'pe-access',
      title: 'What your access covers',
      intro:
        'Project Engineer accounts are set up deliberately narrower than Project Manager accounts. This is the full picture, so you know what to do rather than wondering whether something is broken.',
      topics: [
        {
          id: 'pe-role-limits',
          title: 'What you can and can\'t do',
          summary:
            'Everything on the job record itself is yours. Anything that spends money or commits people is the PM\'s.',
          table: {
            caption: 'Project Engineer access',
            headers: ['You can', 'You can\'t — ask the PM'],
            rows: [
              ['RFIs and submittals', 'Raise equipment requests'],
              ['Change orders', 'Raise receiving orders'],
              ['QA/QC pour cards', 'Add material lines'],
              ['Documents and drawings', 'Edit survey requests'],
              ['Daily logs', 'Change the crew schedule'],
              ['Project details, notes and dates', 'Add or edit a mobilization phase'],
              ['The mobilization checklist', ''],
            ],
          },
          watchOut: [
            'The **Mobilization tab splits down the middle**: you can work the readiness checklist, but you cannot add or change the mobilization phases above it. Same tab, different access — this is the one that catches people.',
            'If you need equipment, receiving or materials raised regularly, that is a conversation with an admin about widening your access, not a workaround.',
          ],
        },
      ],
    },
  ],
};
